// Ported from design/project/App80-BusyQueue.dc.html and App81-Waitlist.dc.html: the two screens behind the
// dashboard's Queue screen and Pause sign-ups switches.
import { useEffect, useState } from "react";
import { useGame, useTicker, type WaitlistPlace } from "../game.js";
import { go } from "../router.js";
import { tg } from "../telegram.js";

const openTelegram = (url: string) => (tg?.openTelegramLink ? tg.openTelegramLink(url) : void window.open(url, "_blank"));

const QUEUE_KEY = "dd:queueUntil";
const QUEUE_DONE = "dd:queueDone";

/** Already let in during this app open. */
export function queueDone(): boolean {
  try {
    return sessionStorage.getItem(QUEUE_DONE) === "1";
  } catch {
    return true;
  }
}

/** When this player's wait ends: picked once per app open, 10 to 30 seconds, so arrivals spread out. */
function queueUntil(): number {
  try {
    const saved = Number(sessionStorage.getItem(QUEUE_KEY));
    if (saved > 0) return saved;
    const until = Date.now() + 10_000 + Math.floor(Math.random() * 20_000);
    sessionStorage.setItem(QUEUE_KEY, String(until));
    return until;
  } catch {
    return Date.now();
  }
}

function Icon({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ marginTop: "14px", display: "flex", justifyContent: "center" }}>
      <div style={{ width: "88px", height: "88px", borderRadius: "28px", background: "rgba(204,255,0,0.1)", border: "1px solid rgba(204,255,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 50px rgba(204,255,0,0.25)" }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#CCFF00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
      </div>
    </div>
  );
}

export function BusyQueue() {
  const { me } = useGame();
  const [until] = useState(queueUntil);
  const [start] = useState(() => until - 30_000);
  const [, setDone] = useState(false);
  useTicker(250);
  const left = Math.max(0, until - Date.now());
  const ready = left === 0;
  const enter = () => {
    try {
      sessionStorage.setItem(QUEUE_DONE, "1");
    } catch {
      /* private mode: the next open waits again */
    }
    setDone(true);
    go("App09", true);
  };
  const secs = Math.ceil(left / 1000);
  return (
    <div className="screen">
      <div className="orbL" aria-hidden="true" style={{ top: "40px", left: "-45px", width: "480px", height: "480px" }}></div>
      <div className="pad squeeze" style={{ height: "100%", paddingBottom: "44px" }}>
        <div className="top"><span className="chip chipN">BUSY RIGHT NOW</span></div>
        <Icon><circle cx="9" cy="8" r="3.5"></circle><path d="M3 20c1-4 3.5-6 6-6s5 2 6 6"></path><circle cx="17" cy="9" r="2.5"></circle><path d="M17 14c2 .3 3.5 2.3 4 6"></path></Icon>
        <h1 className="h1" style={{ marginTop: "22px", textAlign: "center" }}>So many players right now</h1>
        <p className="muted" style={{ margin: "8px 6px 0", textAlign: "center", fontSize: "14px", lineHeight: "21px" }}>We're letting everyone in a few at a time so battles stay fast. You're nearly there.</p>
        <div className="g" style={{ marginTop: "22px", borderRadius: "20px", padding: "18px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
          <span className="lbl" style={{ fontSize: "10px" }}>{ready ? "YOU'RE IN" : "YOU'RE IN IN"}</span>
          <span className="mono lime" role="timer" style={{ fontWeight: "800", fontSize: "44px", lineHeight: "48px" }}>0:{String(secs).padStart(2, "0")}</span>
          <div style={{ width: "100%", height: "6px", borderRadius: "3px", background: "#30363D", overflow: "hidden" }}>
            <div style={{ width: `${Math.min(100, ((Date.now() - start) / (until - start)) * 100)}%`, height: "100%", background: "#CCFF00", transition: "width 0.25s linear" }}></div>
          </div>
        </div>
        <div className="g" style={{ marginTop: "12px", borderRadius: "20px", padding: "4px 0" }}>
          <div className="li"><span className="muted" style={{ flex: "1", fontSize: "13px" }}>Your balance</span><span className="mono" style={{ fontSize: "14px", fontWeight: "800" }}>{me?.balance.available ?? "..."} USDT</span></div>
          <div className="div" style={{ margin: "0 14px" }}></div>
          <div className="li"><span className="muted" style={{ flex: "1", fontSize: "13px" }}>Battles already running</span><span className="lime" style={{ fontSize: "13px", fontWeight: "700" }}>Not affected</span></div>
        </div>
        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: "10px" }}>
          <button onClick={enter} disabled={!ready} className="btnP" style={{ opacity: ready ? 1 : 0.45 }}>LET ME IN</button>
          <button onClick={() => go("App28")} className="btnS">Open wallet</button>
        </div>
      </div>
    </div>
  );
}

const joined = (ms: number) => {
  const d = new Date(ms);
  const today = new Date().toDateString() === d.toDateString();
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return today ? `Today, ${time}` : `${d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}, ${time}`;
};

export function Waitlist({ place }: { place: WaitlistPlace }) {
  const { refresh } = useGame();
  const [checking, setChecking] = useState(false);
  // Every minute the place is asked for again, so it moves without a tap.
  useEffect(() => {
    const t = setInterval(() => void refresh().catch(() => {}), 60_000);
    return () => clearInterval(t);
  }, [refresh]);
  return (
    <div className="screen">
      <div className="orbL" aria-hidden="true" style={{ top: "40px", left: "-45px", width: "480px", height: "480px" }}></div>
      <div className="pad squeeze" style={{ height: "100%", paddingBottom: "44px" }}>
        <div className="top"><span className="chip chipN">WAITLIST</span></div>
        <Icon><path d="M8 6h13M8 12h13M8 18h13"></path><path d="M3 6h.01M3 12h.01M3 18h.01"></path></Icon>
        <h1 className="h1" style={{ marginTop: "22px", textAlign: "center" }}>You're on the list</h1>
        <p className="muted" style={{ margin: "8px 6px 0", textAlign: "center", fontSize: "14px", lineHeight: "21px" }}>DegenDuel is letting new players in a few at a time. The bot messages you the moment it's your turn.</p>
        <div className="g" style={{ marginTop: "22px", borderRadius: "20px", padding: "18px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
          <span className="lbl" style={{ fontSize: "10px" }}>YOUR PLACE</span>
          <span className="mono lime" style={{ fontWeight: "800", fontSize: "44px", lineHeight: "48px" }}>#{place.place.toLocaleString("en-US")}</span>
          <span className="muted" style={{ fontSize: "12px" }}>of {place.of.toLocaleString("en-US")} waiting</span>
        </div>
        <div className="g" style={{ marginTop: "12px", borderRadius: "20px", padding: "4px 0" }}>
          <div className="li"><span className="muted" style={{ flex: "1", fontSize: "13px" }}>Joined the list</span><span style={{ fontSize: "13px" }}>{joined(place.joinedAt)}</span></div>
          <div className="div" style={{ margin: "0 14px" }}></div>
          <div className="li"><span className="muted" style={{ flex: "1", fontSize: "13px" }}>When it's your turn</span><span style={{ fontSize: "13px" }}>A message from the bot</span></div>
        </div>
        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: "10px" }}>
          <button className="btnP" disabled={checking} onClick={() => { setChecking(true); void refresh().catch(() => {}).finally(() => setChecking(false)); }}>{checking ? "CHECKING..." : "CHECK MY PLACE"}</button>
          <button className="btnS" onClick={() => openTelegram("https://t.me/degensduel")}>Join the channel meanwhile</button>
        </div>
      </div>
    </div>
  );
}
